import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';

// Rich text fields (park description/banner, add-on description, invoice terms, ...)
// have gone through three eras of storage format over time:
//   1. plain text (earliest data, before any rich text editor existed)
//   2. Quill Delta JSON, e.g. '{"ops":[{"insert":"hello\n"}]}' (react-quill-v2.0 era)
//   3. plain HTML, e.g. '<p>hello</p>' (current, TipTap era)
// These helpers normalize any of the three into HTML for display/editing so callers
// never need to know which era a given record was saved in.

export function isQuillDelta(value) {
    if (!value || typeof value !== 'string') return false;
    try {
        const parsed = JSON.parse(value);
        return !!(parsed && typeof parsed === 'object' && Array.isArray(parsed.ops));
    } catch {
        return false;
    }
}

function looksLikeHtml(value) {
    return /<[a-z][\s\S]*>/i.test(value ?? '');
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

// True when a stored value (plain text / Quill Delta JSON / HTML) has no visible content.
export function isRichTextEmpty(raw) {
    if (!raw) return true;

    if (isQuillDelta(raw)) {
        const ops = JSON.parse(raw)?.ops ?? [];
        const text = ops.map(op => (typeof op.insert === 'string' ? op.insert : '')).join('');
        return text.trim().length === 0;
    }

    const text = String(raw).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return text.length === 0;
}

// Converts a stored value (plain text / Quill Delta JSON / HTML) into an HTML string.
export function richTextToHtml(raw) {
    if (!raw) return '';

    if (isQuillDelta(raw)) {
        const ops = JSON.parse(raw)?.ops ?? [];
        const converter = new QuillDeltaToHtmlConverter(ops, {});
        return converter.convert();
    }

    if (looksLikeHtml(raw)) {
        return raw;
    }

    // plain text → escape and wrap so it round-trips safely through an HTML editor/viewer
    const escaped = escapeHtml(raw).replace(/\n/g, '<br/>');
    return `<p>${escaped}</p>`;
}
